import { compressImage } from "@/imageOpt";
import { useState } from "react";
import { createPortal } from "react-dom";
import api from "@/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { MessageSquareWarning, Camera, X, Send, LifeBuoy } from "lucide-react";

export default function ReportProblem({ kind = "bug", collapsed = false }) {
  const { t } = useI18n();
  const isHelp = kind === "help";
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [code, setCode] = useState("");
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const f0 = e.target.files?.[0];
    e.target.value = "";
    if (!f0) return;
    const f = await compressImage(f0, 1200, 0.75);
    if (f.size > 4 * 1024 * 1024) { toast.error(t("reportTooBig")); return; }
    const r = new FileReader();
    r.onload = () => setImg({ data: r.result, name: f.name });
    r.readAsDataURL(f);
  };

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.supportReport({ kind, message: msg.trim(), error_code: isHelp ? null : (code.trim() || null), page: window.location.pathname, image_base64: img?.data || null, image_name: img?.name || null });
      toast.success(isHelp ? t("helpSent") : t("reportSent"));
      setOpen(false); setMsg(""); setCode(""); setImg(null);
    } catch (err) { toast.error(err?.response?.data?.detail || t("genericError")); }
    finally { setBusy(false); }
  };

  return (
    <>
      {isHelp ? (
        <button onClick={() => setOpen(true)} className="flex items-start gap-2 text-xs text-white/50 hover:text-white transition-colors text-left" title="italianchef.a@gmail.com" data-testid="sidebar-support">
          <LifeBuoy className="w-4 h-4 shrink-0" />
          {!collapsed && (
            <span className="min-w-0">
              <span className="block">{t("needHelp")}</span>
              <span className="block text-white/40 truncate">italianchef.a@gmail.com</span>
            </span>
          )}
        </button>
      ) : (
        <button onClick={() => setOpen(true)} className="flex items-center gap-2 text-xs text-amber-300/80 hover:text-amber-200 transition-colors" data-testid="report-problem-btn">
          <MessageSquareWarning className="w-4 h-4" /> {t("reportBtn")}
        </button>
      )}
      {open && createPortal(
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/60 p-4" onClick={() => setOpen(false)} data-testid="report-overlay">
          <form onSubmit={send} className="text-foreground bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="report-modal">
            <div className="flex items-center justify-between mb-1">
              <div className="font-semibold text-lg flex items-center gap-2">
                {isHelp ? <LifeBuoy className="w-5 h-5 text-accent" /> : <MessageSquareWarning className="w-5 h-5 text-amber-500" />}
                {isHelp ? t("helpTitle") : t("reportTitle")}
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-1.5 hover:bg-muted rounded-full" data-testid="report-close"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">{isHelp ? t("helpHint") : t("reportHint")}</p>
            <textarea required autoFocus rows={5} className="field w-full resize-none placeholder:text-muted-foreground/50" placeholder={isHelp ? t("helpPlaceholder") : t("reportPlaceholder")} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="report-message" />
            {!isHelp && <input className="field w-full mt-2 mono" placeholder={t("reportErrorCodePh")} value={code} onChange={(e) => setCode(e.target.value)} data-testid="report-error-code" />}
            <label className="mt-2 flex items-center gap-2 text-sm border border-dashed border-border rounded-xl px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors" data-testid="report-attach">
              <Camera className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground truncate">{img ? `📎 ${img.name}` : t("reportAttach")}</span>
              <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="report-file" />
            </label>
            <button type="submit" disabled={busy || msg.trim().length < 5} className="btn-primary w-full mt-4 py-2.5 flex items-center justify-center gap-2 disabled:opacity-40" data-testid="report-send">
              <Send className="w-4 h-4" /> {busy ? "…" : (isHelp ? t("helpSend") : t("reportSend"))}
            </button>
          </form>
        </div>,
        document.body
      )}
    </>
  );
}
